var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Restriction Filter Component
 * Componente de filtro de restricciones mejorado según la imagen proporcionada
 */
import { useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Box, Typography, Chip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { RESTRICTION_OPTIONS } from '@/services/inventoryPlanningService';
// Styled components
var StyledSelect = styled(Select)(function (_a) {
    var theme = _a.theme;
    return ({
        minWidth: 200,
        '& .MuiSelect-multiple': {
            padding: '8px 12px',
        },
    });
});
var FilterChip = styled(Chip)(function (_a) {
    var theme = _a.theme;
    return ({
        margin: '2px',
        height: '24px',
        fontSize: '0.75rem',
    });
});
var CategoryDivider = styled(Divider)(function (_a) {
    var theme = _a.theme;
    return ({
        margin: '8px 0',
        '&::before, &::after': {
            borderColor: theme.palette.primary.main,
        },
    });
});
// Main filter options (como se ve en la imagen)
var MAIN_FILTER_OPTIONS = [
    { value: 'ALL_RESTRICTIONS', label: 'All Restrictions' },
    { value: 'ONLY_AVAILABILITY', label: 'Only Availability' },
    { value: 'RATE_AND_AVAILABILITY', label: 'Rate And Availability' },
];
var RestrictionFilter = function (_a) {
    var selectedRestrictions = _a.selectedRestrictions, onRestrictionChange = _a.onRestrictionChange, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    var _c = useState(false), open = _c[0], setOpen = _c[1];
    var handleChange = function (event) {
        var value = event.target.value;
        // Si se selecciona "All Restrictions", limpiar otros y solo mantener ese
        if (value.includes('ALL_RESTRICTIONS') && !selectedRestrictions.includes('ALL_RESTRICTIONS')) {
            onRestrictionChange(['ALL_RESTRICTIONS']);
            return;
        }
        // Si se deselecciona "All Restrictions", mantener las selecciones específicas
        if (!value.includes('ALL_RESTRICTIONS') && selectedRestrictions.includes('ALL_RESTRICTIONS')) {
            onRestrictionChange(value.filter(function (v) { return v !== 'ALL_RESTRICTIONS'; }));
            return;
        }
        // Para otras selecciones, manejar normalmente
        onRestrictionChange(value);
    };
    var handleClose = function () {
        setOpen(false);
    };
    var handleOpen = function () {
        setOpen(true);
    };
    // Renderizar el valor seleccionado
    var renderValue = function (selected) {
        if (selected.length === 0) {
            return 'Select restrictions';
        }
        if (selected.includes('ALL_RESTRICTIONS')) {
            return 'All Restrictions';
        }
        if (selected.length === 1) {
            var option = __spreadArray(__spreadArray([], MAIN_FILTER_OPTIONS, true), RESTRICTION_OPTIONS, true).find(function (opt) { return opt.value === selected[0]; });
            return (option === null || option === void 0 ? void 0 : option.label) || selected[0];
        }
        return "".concat(selected.length, " restrictions");
    };
    // Categorizar opciones para mostrar
    var availabilityOptions = RESTRICTION_OPTIONS.filter(function (opt) { return opt.category === 'availability'; });
    var bookingOptions = RESTRICTION_OPTIONS.filter(function (opt) { return opt.category === 'booking'; });
    var rateOptions = RESTRICTION_OPTIONS.filter(function (opt) { return opt.category === 'rate'; });
    return (_jsxs(Box, { children: [_jsxs(FormControl, { fullWidth: true, disabled: disabled, children: [_jsx(InputLabel, { children: "".concat(selectedRestrictions.length, " restrictions") }), _jsxs(Select, { multiple: true, value: selectedRestrictions, onChange: handleChange, open: open, onClose: handleClose, onOpen: handleOpen, renderValue: renderValue, sx: {
                            minWidth: 200,
                            '& .MuiSelect-multiple': {
                                padding: '8px 12px',
                            },
                        }, MenuProps: {
                            PaperProps: {
                                style: {
                                    maxHeight: 400,
                                    width: 280,
                                },
                            },
                        }, children: [MAIN_FILTER_OPTIONS.map(function (option) { return (_jsxs(MenuItem, { value: option.value, children: [_jsx(Checkbox, { checked: selectedRestrictions.includes(option.value), size: "small" }), _jsx(ListItemText, { primary: option.label, primaryTypographyProps: { fontSize: '0.875rem' } })] }, option.value)); }), _jsx(CategoryDivider, { children: _jsx(Typography, { variant: "caption", color: "primary", children: "Specific Restrictions" }) }), _jsx(MenuItem, { disabled: true, children: _jsx(Typography, { variant: "caption", color: "textSecondary", sx: { fontWeight: 'bold' }, children: "Availability" }) }), availabilityOptions.map(function (option) { return (_jsxs(MenuItem, { value: option.value, children: [_jsx(Checkbox, { checked: selectedRestrictions.includes(option.value), size: "small", color: "info" }), _jsx(ListItemText, { primary: option.label, primaryTypographyProps: { fontSize: '0.875rem' } })] }, option.value)); }), _jsx(MenuItem, { disabled: true, children: _jsx(Typography, { variant: "caption", color: "textSecondary", sx: { fontWeight: 'bold', mt: 1 }, children: "Booking" }) }), bookingOptions.map(function (option) { return (_jsxs(MenuItem, { value: option.value, children: [_jsx(Checkbox, { checked: selectedRestrictions.includes(option.value), size: "small", color: "warning" }), _jsx(ListItemText, { primary: option.label, primaryTypographyProps: { fontSize: '0.875rem' } })] }, option.value)); }), _jsx(MenuItem, { disabled: true, children: _jsx(Typography, { variant: "caption", color: "textSecondary", sx: { fontWeight: 'bold', mt: 1 }, children: "Rate" }) }), rateOptions.map(function (option) { return (_jsxs(MenuItem, { value: option.value, children: [_jsx(Checkbox, { checked: selectedRestrictions.includes(option.value), size: "small", color: "error" }), _jsx(ListItemText, { primary: option.label, primaryTypographyProps: { fontSize: '0.875rem' } })] }, option.value)); })] })] }), selectedRestrictions.length > 0 && (_jsx(Box, { sx: { mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }, children: selectedRestrictions.map(function (restriction) {
                    var option = __spreadArray(__spreadArray([], MAIN_FILTER_OPTIONS, true), RESTRICTION_OPTIONS, true).find(function (opt) { return opt.value === restriction; });
                    var color = 'default';
                    if (MAIN_FILTER_OPTIONS.find(function (opt) { return opt.value === restriction; })) {
                        color = 'primary';
                    }
                    else {
                        var restrictionOption = RESTRICTION_OPTIONS.find(function (opt) { return opt.value === restriction; });
                        if (restrictionOption) {
                            switch (restrictionOption.category) {
                                case 'availability':
                                    color = 'info';
                                    break;
                                case 'booking':
                                    color = 'warning';
                                    break;
                                case 'rate':
                                    color = 'error';
                                    break;
                            }
                        }
                    }
                    return (_jsx(FilterChip, { label: (option === null || option === void 0 ? void 0 : option.label) || restriction, color: color, size: "small", onDelete: function () {
                            var newSelected = selectedRestrictions.filter(function (r) { return r !== restriction; });
                            onRestrictionChange(newSelected);
                        } }, restriction));
                }) }))] }));
};
export default RestrictionFilter;
